import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { Card } from "../components/ui/Card.js";
import { Badge } from "../components/ui/Badge.js";
import { useToast } from "../hooks/use-toast.js";
import { useMutation, useSubscription } from "@apollo/client";
import {
  SIMPLE_SIGNUP_SUBSCRIPTION,
  VERIFY_OTP_AND_LOGIN,
  ERASE_USER_PROFILE,
} from "../lib/graphqlOperations.js";
import { Navbar } from "../components/layout/Navbar.js";
import Footer from "../components/layout/Footer.js";
import {
  Trash2,
  ShieldAlert,
  Phone,
  KeyRound,
  Check,
  ChevronRight,
  AlertTriangle,
  ArrowLeft,
  Loader2,
} from "lucide-react";

const DELETION_REASONS = [
  { value: "not_using", label: "I no longer use the app" },
  { value: "privacy", label: "Privacy concerns" },
  { value: "bad_experience", label: "Bad experience with the service" },
  { value: "found_alternative", label: "Found a better alternative" },
  { value: "too_many_notifications", label: "Too many notifications" },
  { value: "temporary", label: "Temporary break — I might come back" },
  { value: "other", label: "Other reason" },
];

// Steps: reason → phone → otp → confirm → done
const STEPS = {
  REASON: 0,
  PHONE: 1,
  OTP: 2,
  CONFIRM: 3,
  DONE: 4,
};

export default function DeleteAccount() {
  const [step, setStep] = useState(STEPS.REASON);
  const [reason, setReason] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [userId, setUserId] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [otpTimer, setOtpTimer] = useState(0);
  const [subscriptionPhone, setSubscriptionPhone] = useState(null);
  const otpRefs = useRef([]);
  const { toast } = useToast();

  // ─── OTP timer countdown ────────────────────────────────────────────────────
  useEffect(() => {
    if (otpTimer <= 0) return;
    const timer = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [otpTimer]);

  // ─── Subscription: triggers OTP send ────────────────────────────────────────
  const { loading: otpSending } = useSubscription(SIMPLE_SIGNUP_SUBSCRIPTION, {
    variables: { phone: subscriptionPhone },
    skip: !subscriptionPhone,
    onData: ({ data: { data } }) => {
      const result = data?.simpleSignupSubscription;
      if (result?.success) {
        toast({
          title: "OTP Sent",
          description: `Verification code sent to ${phone}`,
        });
        setOtpTimer(60);
        setStep(STEPS.OTP);
      } else {
        const msg = result?.failureReason || result?.message || "Failed to send OTP";
        toast({
          title: "Error",
          description: msg,
          variant: "destructive",
        });
      }
      // Stop the subscription after receiving
      setSubscriptionPhone(null);
    },
    onError: (error) => {
      toast({
        title: "Error sending OTP",
        description: error.message,
        variant: "destructive",
      });
      setSubscriptionPhone(null);
    },
  });

  // ─── Verify OTP mutation ────────────────────────────────────────────────────
  const [verifyOtp, { loading: verifyingOtp }] = useMutation(VERIFY_OTP_AND_LOGIN, {
    onCompleted: (data) => {
      const result = data?.verifyOtpAndLogin;
      if (result?.success) {
        setUserId(result.user?.id);
        setAuthToken(result.token?.authToken);
        setStep(STEPS.CONFIRM);
      } else {
        const msg = result?.message || result?.errors?.[0]?.message || "Invalid OTP. Please try again.";
        toast({
          title: "Verification Failed",
          description: msg,
          variant: "destructive",
        });
        setOtp(["", "", "", "", "", ""]);
      }
    },
    onError: (error) => {
      toast({
        title: "Verification Failed",
        description: error.message || "Please check the OTP and try again.",
        variant: "destructive",
      });
      setOtp(["", "", "", "", "", ""]);
    },
  });

  // ─── Delete account mutation ────────────────────────────────────────────────
  const [eraseProfile, { loading: deleting }] = useMutation(ERASE_USER_PROFILE, {
    context: {
      headers: {
        authorization: authToken ? `Bearer ${authToken}` : "",
      },
    },
    onCompleted: (data) => {
      const result = data?.eraseUserProfile;
      if (result?.success) {
        setStep(STEPS.DONE);
      } else {
        const msg = result?.message || result?.errors?.[0]?.message || "Failed to delete account.";
        toast({
          title: "Deletion Failed",
          description: msg,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Deletion Failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleSendOtp = () => {
    const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
    if (!cleanPhone || cleanPhone.length !== 10) {
      toast({
        title: "Invalid Phone",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }
    setSubscriptionPhone(cleanPhone);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = () => {
    const otpStr = otp.join("");
    if (otpStr.length < 6) {
      toast({ title: "Enter complete OTP", description: "Please enter all 6 digits.", variant: "destructive" });
      return;
    }
    const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
    verifyOtp({ variables: { phone: cleanPhone, otp: otpStr } });
  };

  const handleDeleteAccount = () => {
    if (!userId) return;
    eraseProfile({ variables: { userId } });
  };

  const handleResendOtp = () => {
    setOtp(["", "", "", "", "", ""]);
    const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
    setSubscriptionPhone(cleanPhone);
  };

  // ─── Step indicators ────────────────────────────────────────────────────────
  const stepLabels = ["Reason", "Phone", "Verify", "Confirm"];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
            scale: [1, 1.25, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-[5%] -left-[5%] w-[45%] h-[45%] bg-destructive/5 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -40, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-[10%] -right-[5%] w-[35%] h-[35%] bg-accent/10 rounded-full blur-[120px]"
        />
      </div>

      <div className="relative z-10">
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-destructive/5 via-transparent to-accent/10" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 relative">

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10 space-y-6"
          >
            <Badge variant="secondary" className="px-5 py-2.5 bg-destructive/10 text-destructive border-destructive/20 font-bold">
              <ShieldAlert className="w-5 h-5 mr-3" />
              Account Security
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
              Delete Your Account
            </h1>
            <p className="text-lg text-muted-foreground/80 max-w-lg mx-auto leading-relaxed">
              We're sorry to see you go. This action is permanent and will erase
              all your data from our servers.
            </p>
          </motion.div>

          {/* Progress Steps */}
          {step < STEPS.DONE && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-center gap-2 mb-12"
            >
              {stepLabels.map((label, i) => (
                <div key={label} className="flex items-center">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500
                        ${i < step
                          ? "bg-primary text-primary-foreground"
                          : i === step
                          ? "bg-primary text-primary-foreground ring-[6px] ring-primary/20 shadow-glow shadow-primary/30"
                          : "bg-muted text-muted-foreground"
                        }`}
                    >
                      {i < step ? <Check className="w-5 h-5" /> : i + 1}
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${i <= step ? "text-foreground" : "text-muted-foreground/50"}`}>
                      {label}
                    </span>
                  </div>
                  {i < stepLabels.length - 1 && (
                    <div className={`w-12 md:w-16 h-1 mx-2 mb-6 rounded-full transition-colors duration-500 ${i < step ? "bg-primary" : "bg-muted"}`} />
                  )}
                </div>
              ))}
            </motion.div>
          )}

          {/* Step 1: Select Reason */}
          {step === STEPS.REASON && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <Card className="p-8 md:p-10 border-border/50 shadow-soft bg-card/50 backdrop-blur-xl">
                <h2 className="font-display text-2xl font-bold mb-2">
                  Why are you leaving?
                </h2>
                <p className="text-muted-foreground mb-8">
                  Your feedback is vital to our journey.
                </p>
                <div className="space-y-3">
                  {DELETION_REASONS.map((r) => (
                    <motion.button
                      key={r.value}
                      type="button"
                      whileHover={{ x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setReason(r.value)}
                      className={`w-full text-left px-5 py-4 rounded-2xl border text-sm font-bold transition-all duration-300
                        ${reason === r.value
                          ? "border-primary bg-primary/10 text-primary shadow-glow shadow-primary/10"
                          : "border-border/60 bg-background/50 hover:border-primary/50 text-foreground"
                        }`}
                    >
                      {r.label}
                    </motion.button>
                  ))}
                </div>
                <Button
                  className="w-full mt-10 h-14 rounded-2xl text-lg font-bold shadow-glow shadow-primary/20 hover:-translate-y-1 transition-all"
                  size="lg"
                  disabled={!reason}
                  onClick={() => setStep(STEPS.PHONE)}
                >
                  Confirm & Continue
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Enter Phone */}
          {step === STEPS.PHONE && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="p-8 md:p-10 border-border/50 shadow-soft bg-card/50 backdrop-blur-xl">
                <button
                  onClick={() => setStep(STEPS.REASON)}
                  className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-primary mb-6 transition-colors group"
                >
                  <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Back
                </button>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-display text-2xl font-bold">Verify Identity</h2>
                </div>
                <p className="text-muted-foreground mb-8">
                  Enter the phone number linked to your Quro account. We'll send a
                  one-time verification code.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="h-14 flex items-center text-sm font-bold text-foreground bg-muted/50 px-5 rounded-2xl border border-border/50">
                      +91
                    </div>
                    <Input
                      type="tel"
                      placeholder="9999999999"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, "").slice(0, 15))}
                      maxLength={10}
                      className="flex-1 h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md shadow-soft"
                    />
                  </div>
                  <Button
                    className="w-full h-14 rounded-2xl text-lg font-bold shadow-glow shadow-primary/20 hover:-translate-y-1 transition-all"
                    size="lg"
                    disabled={otpSending}
                    onClick={handleSendOtp}
                  >
                    {otpSending ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                        Routing OTP...
                      </>
                    ) : (
                      <>
                        Get Verification Code
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Enter OTP */}
          {step === STEPS.OTP && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="p-8 md:p-10 border-border/50 shadow-soft bg-card/50 backdrop-blur-xl">
                <button
                  onClick={() => { setStep(STEPS.PHONE); setOtp(["","","","","",""]); }}
                  className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-primary mb-6 transition-colors group"
                >
                  <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Change Number
                </button>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <KeyRound className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="font-display text-2xl font-bold">Six-Digit Check</h2>
                </div>
                <p className="text-muted-foreground mb-8">
                  We sent a code to <span className="font-bold text-foreground">+91 {phone}</span>
                </p>
                <div className="flex justify-between gap-2 mb-10">
                  {otp.map((digit, i) => (
                    <motion.input
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-12 h-16 text-center text-2xl font-bold rounded-2xl border border-border/60 bg-background/50 focus:border-primary focus:ring-4 focus:ring-primary/20 outline-none transition-all shadow-inner"
                    />
                  ))}
                </div>
                <Button
                  className="w-full h-14 rounded-2xl text-lg font-bold shadow-glow shadow-primary/20 hover:-translate-y-1 transition-all"
                  size="lg"
                  disabled={otp.join("").length < 6 || verifyingOtp}
                  onClick={handleVerifyOtp}
                >
                  {verifyingOtp ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    "Authorize Deletion"
                  )}
                </Button>
                <div className="text-center mt-6">
                  {otpTimer > 0 ? (
                    <p className="text-sm font-bold text-muted-foreground">
                      Resend in <span className="text-primary">{otpTimer}s</span>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="text-sm text-primary hover:text-primary/80 font-bold underline underline-offset-4"
                    >
                      Resend Verification Code
                    </button>
                  )}
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 4: Confirm Deletion */}
          {step === STEPS.CONFIRM && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Card className="p-8 md:p-10 border-destructive/20 shadow-soft bg-destructive/5 backdrop-blur-xl">
                <div className="text-center space-y-8">
                  <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto animate-pulse">
                    <AlertTriangle className="w-10 h-10 text-destructive" />
                  </div>
                  <div className="space-y-4">
                    <h2 className="font-display text-3xl font-bold text-destructive">
                      The Point of No Return
                    </h2>
                    <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
                      This action is <strong className="text-destructive">permanent</strong>. Your entire legacy on Quro—orders, points, and account data—will be wiped.
                    </p>
                  </div>
                  <div className="bg-background/80 backdrop-blur-sm border border-destructive/10 rounded-[2rem] p-8 text-left space-y-4">
                    <p className="font-bold text-destructive flex items-center gap-2">
                       <ShieldAlert className="w-4 h-4" /> Immediate Consequences:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm font-medium text-muted-foreground">
                      <li className="flex items-center gap-2">• Profile Erasure</li>
                      <li className="flex items-center gap-2">• Order History Wipe</li>
                      <li className="flex items-center gap-2">• Payment Method Purge</li>
                      <li className="flex items-center gap-2">• Referral Loss</li>
                    </ul>
                  </div>
                  <div className="flex flex-col gap-4 pt-4">
                    <Button
                      variant="destructive"
                      size="lg"
                      className="w-full h-15 h-14 rounded-2xl text-lg font-bold shadow-glow shadow-destructive/20 hover:scale-[1.02] transition-all"
                      disabled={deleting}
                      onClick={handleDeleteAccount}
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                          Finalizing Deletion...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-5 h-5 mr-3" />
                          Destroy My Account
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full h-14 rounded-2xl font-bold bg-background/50 hover:bg-background transition-all"
                      onClick={() => {
                        setStep(STEPS.REASON);
                        setReason("");
                        setPhone("");
                        setOtp(["", "", "", "", "", ""]);
                        setUserId(null);
                        setAuthToken(null);
                      }}
                    >
                      Stay - I've Changed My Mind
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 5: Success */}
          {step === STEPS.DONE && (
            <Card className="p-6 md:p-8 border-border/50 shadow-md">
              <div className="text-center space-y-5 py-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-display text-xl font-bold">
                    Account Deleted
                  </h2>
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    Your account and all associated data have been permanently
                    deleted. We're sorry to see you go.
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  If you ever want to come back, you can always create a new
                  account.
                </p>
                <Button variant="outline" onClick={() => (window.location.href = "/")}>
                  Go to Home Page
                </Button>
              </div>
            </Card>
          )}
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
